import { apiClient, OAuthInteractionInvalidError } from 'modules/api-client';
import { BaseCard } from 'modules/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { loadPage } from 'utils/location';
import { AuthBaseCardHeader } from '../../components/AuthBaseCardHeader/AuthBaseCardHeader';
import { AuthLayout } from '../../components/AuthLayout/AuthLayout';
import {
  type Credentials,
  PasswordSignInForm,
} from '../../components/PasswordSignInForm/PasswordSignInForm';
import { ErrorMessage } from '../index.css';

const INTERACTION_UID_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * Sign-in step of the public MCP OAuth flow. It never touches the webapp
 * session: the API answers with a URL back to the OAuth server, reached with a
 * full page load so that the OAuth server receives its own cookie.
 */
export const OAuthSignInPage = () => {
  const { t } = useTranslation('user', { keyPrefix: 'user.oauthSignIn' });
  const [searchParams] = useSearchParams();
  const interaction = searchParams.get('interaction') ?? '';
  const [isInteractionRejected, setIsInteractionRejected] = useState(false);

  const isLinkInvalid =
    !INTERACTION_UID_PATTERN.test(interaction) || isInteractionRejected;

  const login = async (credentials: Credentials) => {
    try {
      const { redirectUrl } = await apiClient.user.loginForOAuthInteraction({
        ...credentials,
        oauthInteraction: interaction,
      });
      loadPage(redirectUrl);
    } catch (error) {
      if (error instanceof OAuthInteractionInvalidError) {
        setIsInteractionRejected(true);
        return;
      }
      throw error;
    }
  };

  return (
    <AuthLayout>
      <BaseCard>
        <AuthBaseCardHeader title={t('header')} />
        {isLinkInvalid ? (
          <ErrorMessage role="alert">{t('invalidLink')}</ErrorMessage>
        ) : (
          <PasswordSignInForm
            defaultEmail=""
            userExists
            login={login}
            withResetPasswordLink={false}
          />
        )}
      </BaseCard>
    </AuthLayout>
  );
};
